'use client'

import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { usePlayerAuth } from './player-auth-provider'

/**
 * Tenant/identity isolation for the PLAY realm.
 *
 * WorkspaceCacheGuard is inert here: it watches the admin workspace store
 * (activeWorkspaceId / activeCompanyId), and the play realm has no workspace
 * store — its identity is the LEARNER, carried by the recipient JWT. So a change
 * of learner evicted nothing.
 *
 * That is invisible while the module caches nothing, and becomes a real leak the
 * moment play surfaces are on React Query: an admin who impersonates learner A,
 * returns, and impersonates learner B (or a shared/kiosk browser where one
 * learner signs out and another signs in) would render A's dashboard KPIs,
 * library history, knowledge profile and leaderboard row under B's identity
 * until every query happened to refetch.
 *
 * Mirrors WorkspaceCacheGuard deliberately, including its first-settle rule: the
 * initial identity is not a SWITCH, and evicting there would cancel the very
 * first in-flight queries of the session.
 */
export function PlayCacheGuard() {
  const queryClient = useQueryClient()
  const { user, loading } = usePlayerAuth()
  const prev = useRef<string | null>(null)

  useEffect(() => {
    // Wait for auth to settle — a transient null during hydration is not a
    // sign-out, and treating it as one would clear the cache on every mount.
    if (loading) return
    const id = user?.id ?? null

    if (prev.current === null) {
      if (id) prev.current = id
      return
    }
    if (prev.current === id) return

    prev.current = id
    // Identity actually changed (impersonation swap, or a different learner on
    // this browser) → drop every cached server response so the previous
    // learner's data can never render under the new one during refetch.
    queryClient.clear()
  }, [user?.id, loading, queryClient])

  return null
}
